import React from 'react';
import { Shield, AlertTriangle, CheckCircle } from 'lucide-react';

export interface ContextOptimizationInfo {
  confidence_score: number;
  relevant_fields: string[];
  focus_areas: string[];
  optimized: boolean;
}

interface ConfidenceIndicatorProps {
  data: ContextOptimizationInfo;
  showDetails?: boolean;
  compact?: boolean;
}

/**
 * Displays confidence score and context optimization info for survey questions.
 * Shows how reliably persona data can answer each question.
 */
const ConfidenceIndicator: React.FC<ConfidenceIndicatorProps> = ({
  data,
  showDetails = false,
  compact = false,
}) => {
  const { confidence_score, relevant_fields, focus_areas } = data;
  
  // Determine confidence level and styling
  const getConfidenceLevel = (score: number) => {
    if (score >= 0.8) return { level: 'high', label: 'High Confidence', color: 'emerald' };
    if (score >= 0.5) return { level: 'medium', label: 'Medium Confidence', color: 'blue' };
    return { level: 'low', label: 'Low Confidence', color: 'amber' };
  };
  
  const { level, label, color } = getConfidenceLevel(confidence_score);
  const percentage = Math.round(confidence_score * 100);
  
  // Color classes based on confidence level
  const colorClasses = {
    emerald: {
      bg: 'bg-emerald-50',
      border: 'border-emerald-200',
      text: 'text-emerald-700',
      icon: 'text-emerald-600',
      bar: 'bg-emerald-500',
    },
    blue: {
      bg: 'bg-blue-50',
      border: 'border-blue-200',
      text: 'text-blue-700',
      icon: 'text-blue-600',
      bar: 'bg-blue-500',
    },
    amber: {
      bg: 'bg-amber-50',
      border: 'border-amber-200',
      text: 'text-amber-700',
      icon: 'text-amber-600',
      bar: 'bg-amber-500',
    },
  };
  
  const colors = colorClasses[color as keyof typeof colorClasses];
  
  // Icon based on confidence level
  const ConfidenceIcon = level === 'high' ? CheckCircle : level === 'medium' ? Shield : AlertTriangle;
  
  if (compact) {
    return (
      <div 
        className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-full ${colors.bg} ${colors.border} border`}
        title={`${label}: ${percentage}% confidence that persona data can answer this question`}
      >
        <ConfidenceIcon className={`h-3.5 w-3.5 ${colors.icon}`} />
        <span className={`text-xs font-medium ${colors.text}`}>{percentage}%</span>
      </div>
    );
  }
  
  return (
    <div className={`rounded-lg ${colors.bg} ${colors.border} border p-3`}>
      {/* Header */}
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <ConfidenceIcon className={`h-4 w-4 ${colors.icon}`} />
          <span className={`text-sm font-medium ${colors.text}`}>{label}</span>
        </div>
        <span className={`text-sm font-semibold ${colors.text}`}>{percentage}%</span>
      </div>
      
      {/* Progress Bar */}
      <div className="h-1.5 bg-gray-200 rounded-full overflow-hidden mb-2">
        <div 
          className={`h-full ${colors.bar} rounded-full transition-all duration-300`}
          style={{ width: `${percentage}%` }}
        />
      </div>
      
      {/* Description */}
      <p className="text-xs text-gray-600 mb-2">
        {level === 'high' && 'Persona data is well-suited to answer this question.'}
        {level === 'medium' && 'Persona data partially covers this question\'s requirements.'}
        {level === 'low' && 'Persona data may not fully address this question.'}
      </p>
      
      {/* Details (expandable) */}
      {showDetails && (
        <div className="mt-3 pt-3 border-t border-gray-200 space-y-2">
          {relevant_fields.length > 0 && (
            <div>
              <p className="text-xs font-medium text-gray-500 mb-1">Relevant Fields Used:</p>
              <div className="flex flex-wrap gap-1">
                {relevant_fields.slice(0, 5).map((field, idx) => (
                  <span 
                    key={idx}
                    className="px-2 py-0.5 bg-white border border-gray-200 rounded text-xs text-gray-600"
                  >
                    {field}
                  </span>
                ))}
                {relevant_fields.length > 5 && (
                  <span className="px-2 py-0.5 text-xs text-gray-500">
                    +{relevant_fields.length - 5} more
                  </span>
                )}
              </div>
            </div>
          )}
          
          {focus_areas.length > 0 && (
            <div>
              <p className="text-xs font-medium text-gray-500 mb-1">Focus Areas:</p>
              <ul className="text-xs text-gray-600 space-y-0.5">
                {focus_areas.slice(0, 3).map((area, idx) => (
                  <li key={idx} className="flex items-start gap-1">
                    <span className="text-gray-400">•</span>
                    <span>{area}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ConfidenceIndicator;

